'use client'

import { useState } from 'react'
import type { CSSProperties } from 'react'
import Link from 'next/link'
import './mojo-ai-case-study.css'

/**
 * Mojo AI case study.
 *
 * A port of `public/radar-branding/mojo-ai-new.html`. That file is the design
 * contract: markup, class names, copy and section order match it, so the two
 * can be diffed. Styling lives in the sibling `mojo-ai-case-study.css`, scoped
 * under `.mojo-cs`.
 *
 * Renders full-bleed -- `WorkPage`'s hero and container are skipped for this
 * slug (see `fullBleedProjects` in ./index.ts) because it ships its own hero.
 *
 * The variation studio was an IIFE writing innerHTML; it is React state here,
 * with the same four templates, four treatments, four formats and the same
 * deterministic score function. Nothing is random -- `scoreFor` derives from
 * the selection's char codes, so the server and client agree.
 *
 * NOTE ON COLOUR: the previewed creative is ARTWORK, not chrome. Its palette
 * comes from the template and treatment data below and is identical in both
 * themes, exactly as a photograph would be. Only the surrounding studio UI
 * follows the theme.
 *
 * Dropped: the mock's header, footer, contact strip, closing CTA, prev/next
 * nav and scroll-progress bar (site chrome renders those), and the "Project
 * Details Coming Soon" section.
 */

interface Palette {
  bg: string
  accent: string
  secondary: string
  ink: string
}

interface Template extends Palette {
  id: string
  name: string
  type: string
  swatch: string
}

const TEMPLATES: Template[] = [
  {
    id: 'launch',
    name: 'Launch system',
    type: 'Product release',
    bg: '#7e64fc',
    accent: '#fdc304',
    secondary: '#f90b8a',
    ink: '#050506',
    swatch: '#7e64fc',
  },
  {
    id: 'signal',
    name: 'Signal campaign',
    type: 'Thought leadership',
    bg: '#73d8f5',
    accent: '#f90b8a',
    secondary: '#7e64fc',
    ink: '#050506',
    swatch: '#73d8f5',
  },
  {
    id: 'proof',
    name: 'Proof series',
    type: 'Feature story',
    bg: '#fdc304',
    accent: '#7e64fc',
    secondary: '#f90b8a',
    ink: '#050506',
    swatch: '#fdc304',
  },
  {
    id: 'night',
    name: 'Night edition',
    type: 'Editorial set',
    bg: '#111116',
    accent: '#f90b8a',
    secondary: '#73d8f5',
    ink: '#f4f1f5',
    swatch: '#111116',
  },
]

interface Treatment extends Palette {
  id: string
  name: string
}

const TREATMENTS: Treatment[] = [
  {
    id: 'a',
    name: 'Variant A',
    bg: '#7e64fc',
    accent: '#fdc304',
    secondary: '#f90b8a',
    ink: '#050506',
  },
  {
    id: 'b',
    name: 'Variant B',
    bg: '#f90b8a',
    accent: '#73d8f5',
    secondary: '#fdc304',
    ink: '#050506',
  },
  {
    id: 'c',
    name: 'Variant C',
    bg: '#fdc304',
    accent: '#7e64fc',
    secondary: '#73d8f5',
    ink: '#050506',
  },
  {
    id: 'd',
    name: 'Variant D',
    bg: '#111116',
    accent: '#f90b8a',
    secondary: '#7e64fc',
    ink: '#f4f1f5',
  },
]

const COPY: Record<string, { headline: string; body: string; cta: string }> = {
  speed: {
    headline: 'Ship the idea.',
    body: 'Turn one approved system into a useful range of launch-ready directions.',
    cta: 'Start building →',
  },
  control: {
    headline: 'Keep the rules.',
    body: 'Make more creative without losing the choices that make the brand recognizable.',
    cta: 'See the system →',
  },
  range: {
    headline: 'More useful range.',
    body: 'Explore formats, messages, and treatments without starting each asset from zero.',
    cta: 'Open the range →',
  },
  ownership: {
    headline: 'Own the system.',
    body: 'Keep the master template, design logic, and final creative decisions with your team.',
    cta: 'Build your library →',
  },
}

const MESSAGES = [
  { value: 'speed', label: 'Speed to launch' },
  { value: 'control', label: 'Creative control' },
  { value: 'range', label: 'More useful range' },
  { value: 'ownership', label: 'Own the system' },
]

const AUDIENCES = [
  'Founders',
  'Product teams',
  'Design leaders',
  'Growth teams',
]

const FORMATS: Record<string, { label: string; ratio: string; short: string }> =
  {
    square: { label: '1080 × 1080', ratio: '1 / 1', short: '1:1' },
    portrait: { label: '1080 × 1350', ratio: '4 / 5', short: '4:5' },
    story: { label: '1080 × 1920', ratio: '9 / 16', short: '9:16' },
    wide: { label: '1200 × 628', ratio: '1.91 / 1', short: '1.91:1' },
  }

/** Deterministic, not random: the same selection always yields the same score,
 *  so the server render and the client hydration agree. */
const scoreFor = (
  template: string,
  treatment: string,
  format: string,
  key: string
) =>
  82 +
  ((template.charCodeAt(0) +
    treatment.charCodeAt(0) +
    format.charCodeAt(0) +
    key.length) %
    16)

export default function MojoAiProjectContent() {
  const [template, setTemplate] = useState('launch')
  const [treatment, setTreatment] = useState('a')
  const [format, setFormat] = useState('square')
  const [audience, setAudience] = useState(AUDIENCES[0])
  const [message, setMessage] = useState('speed')

  const base = TEMPLATES.find(t => t.id === template) ?? TEMPLATES[0]
  // Variant A always mirrors the chosen template; B-D are fixed alternates.
  const variants = TREATMENTS.map((v, i) =>
    i === 0
      ? {
          ...v,
          bg: base.bg,
          accent: base.accent,
          secondary: base.secondary,
          ink: base.ink,
        }
      : v
  )
  const active = variants.find(v => v.id === treatment) ?? variants[0]
  const fmt = FORMATS[format]
  const copy = COPY[message]
  const messageLabel = MESSAGES.find(m => m.value === message)?.label ?? ''

  const audit: [string, number, string][] = [
    ['Brand color', scoreFor(template, treatment, format, 'color'), '#5dd4af'],
    [
      'Type hierarchy',
      scoreFor(template, treatment, format, 'type'),
      '#73d8f5',
    ],
    ['Safe-area fit', scoreFor(template, treatment, format, format), '#fdc304'],
    ['Contrast', scoreFor(template, treatment, format, treatment), '#f90b8a'],
  ]

  const chooseTemplate = (id: string) => {
    setTemplate(id)
    setTreatment('a')
  }

  return (
    <div className='mojo-cs'>
      <section className='mojo-hero' id='overview'>
        <div className='mojo-wrap mojo-hero-grid'>
          <div className='mojo-hero-copy'>
            <div className='mojo-badges'>
              <span className='mojo-badge'>Product Design</span>
              <span className='mojo-badge'>2025</span>
            </div>
            <h1 className='mojo-display'>Mojo AI</h1>
            <p className='mojo-hero-lead'>
              Explore controlled, on-brand ad variations from one master
              template. This portfolio concept demonstrates AI-assisted content
              routes and design checks while keeping human review in the loop.
            </p>
            <div className='mojo-hero-actions'>
              <a className='mojo-btn mojo-btn-yellow' href='#studio'>
                Explore concept studio <span aria-hidden='true'>↓</span>
              </a>
              <Link className='mojo-btn' href='/projects'>
                View all projects <span aria-hidden='true'>↗</span>
              </Link>
            </div>
            <div className='mojo-tech' aria-label='Technologies'>
              <span>TypeScript</span>
              <span>Figma Plugin API</span>
              <span>OpenAI GPT-5</span>
              <span>shadcn/ui</span>
            </div>
            <p className='mojo-source-note'>
              <strong>Demonstration boundary:</strong> The interactive studio
              uses fixed synthetic data. It does not generate or publish files,
              and this case study does not claim a measured production output
              benchmark.
            </p>
          </div>
          <div className='mojo-hero-art'>
            <figure className='mojo-loom' aria-labelledby='loom-caption'>
              <div className='mojo-loom-head'>
                <strong>
                  <i className='mojo-live-dot'></i>Variation loom
                </strong>
                <span>Master → Brand-safe outputs</span>
              </div>
              <div className='mojo-master-card'>
                <i className='mojo-burst'></i>
                <div className='mojo-master-copy'>
                  <strong>
                    Build
                    <br />
                    boldly.
                  </strong>
                  <span>Campaign / Launch 01</span>
                </div>
              </div>
              <div className='mojo-thread'>
                <i></i>
              </div>
              <div className='mojo-variant-row' aria-hidden='true'>
                <div className='mojo-mini' data-size='1:1 · SOCIAL'>
                  <i></i>
                </div>
                <div className='mojo-mini' data-size='4:5 · FEED'>
                  <i></i>
                </div>
                <div className='mojo-mini' data-size='9:16 · STORY'>
                  <i></i>
                </div>
                <div className='mojo-mini' data-size='1.91:1 · DISPLAY'>
                  <i></i>
                </div>
              </div>
              <figcaption className='mojo-loom-foot' id='loom-caption'>
                <span>Concept demo · synthetic creative set</span>
                <span>4 formats · one brand system</span>
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section className='mojo-truth'>
        <div className='mojo-wrap mojo-truth-grid'>
          <div className='mojo-truth-intro'>
            <p className='mojo-label'>What the case study demonstrates</p>
            <p>
              One master template anchors controlled format, audience, message,
              and visual-treatment variations in the illustrative studio below.
            </p>
          </div>
          <div className='mojo-truth-cell'>
            <strong>1</strong>
            <span>Master template anchors every illustrated variation</span>
          </div>
          <div className='mojo-truth-cell'>
            <strong>4</strong>
            <span>Technologies listed on the live page</span>
          </div>
          <div className='mojo-truth-cell'>
            <strong>2025</strong>
            <span>Published project year</span>
          </div>
        </div>
      </section>

      <section className='mojo-section'>
        <div className='mojo-wrap'>
          <div className='mojo-section-head'>
            <div>
              <p className='mojo-label'>The creative bottleneck</p>
              <h2 className='mojo-display'>
                The campaign changes. The brand should not.
              </h2>
            </div>
            <p className='mojo-section-copy'>
              Creative teams need more formats, more messages, and more audience
              variations without rebuilding every composition or letting the
              visual system drift.
            </p>
          </div>
          <div className='mojo-split-board'>
            <article className='mojo-split-side'>
              <p className='mojo-label'>Manual production</p>
              <h3>Every output becomes a new file.</h3>
              <ul className='mojo-split-list'>
                <li>
                  <i>A</i>
                  <span>Resize the layout for every placement.</span>
                </li>
                <li>
                  <i>B</i>
                  <span>Rewrite copy without shared constraints.</span>
                </li>
                <li>
                  <i>C</i>
                  <span>Re-check spacing, contrast, and brand rules.</span>
                </li>
                <li>
                  <i>D</i>
                  <span>Repeat the same review across the set.</span>
                </li>
              </ul>
            </article>
            <div className='mojo-split-arrow' aria-hidden='true'>
              →
            </div>
            <article className='mojo-split-side'>
              <p className='mojo-label'>Variation system</p>
              <h3>One template becomes a controlled range.</h3>
              <ul className='mojo-split-list'>
                <li>
                  <i>A</i>
                  <span>Format rules adapt the composition.</span>
                </li>
                <li>
                  <i>B</i>
                  <span>Message routes stay within a voice system.</span>
                </li>
                <li>
                  <i>C</i>
                  <span>Design analysis flags visual drift.</span>
                </li>
                <li>
                  <i>D</i>
                  <span>Humans choose, refine, and publish.</span>
                </li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      <section
        className='mojo-section'
        id='studio'
        aria-labelledby='studio-heading'
      >
        <div className='mojo-wrap'>
          <div className='mojo-section-head'>
            <p className='mojo-label'>Variation studio</p>
            <h2 className='mojo-display' id='studio-heading'>
              Make the range. Keep the rules.
            </h2>
            <p className='mojo-micro'>
              Concept demo · synthetic campaign data · no files are generated or
              published
            </p>
          </div>

          <div className='mojo-studio'>
            <div className='mojo-studio-top'>
              <div className='mojo-studio-title'>
                <span className='mojo-demo-flag'>Demo</span>
                Mojo AI / Variation Studio
              </div>
              <p>Illustrative creative workspace · fixed local data</p>
            </div>

            <div className='mojo-studio-layout'>
              <aside className='mojo-studio-panel'>
                <div className='mojo-panel-head'>01 · Master template</div>
                <div className='mojo-template-list'>
                  {TEMPLATES.map(t => (
                    <button
                      key={t.id}
                      className='mojo-template-button'
                      type='button'
                      aria-pressed={t.id === template}
                      onClick={() => chooseTemplate(t.id)}
                    >
                      <i
                        className='mojo-thumb'
                        style={
                          {
                            '--swatch': t.swatch,
                            '--accent': t.accent,
                          } as CSSProperties
                        }
                      />
                      <span>
                        <strong>{t.name}</strong>
                        <span>{t.type}</span>
                      </span>
                    </button>
                  ))}
                </div>

                <div className='mojo-control-set'>
                  <span>Format</span>
                  <div className='mojo-control-grid'>
                    {Object.entries(FORMATS).map(([key, f]) => (
                      <button
                        key={key}
                        className='mojo-choice'
                        type='button'
                        aria-pressed={key === format}
                        onClick={() => setFormat(key)}
                      >
                        {f.short}
                      </button>
                    ))}
                  </div>
                </div>

                <label className='mojo-control-set'>
                  <span>Audience</span>
                  <select
                    className='mojo-field'
                    value={audience}
                    onChange={e => setAudience(e.target.value)}
                  >
                    {AUDIENCES.map(a => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </select>
                </label>

                <label className='mojo-control-set'>
                  <span>Message route</span>
                  <select
                    className='mojo-field'
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                  >
                    {MESSAGES.map(m => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </label>
              </aside>

              <section
                className='mojo-studio-panel mojo-canvas-panel'
                aria-label='Creative preview'
              >
                <div className='mojo-canvas-toolbar'>
                  <span>
                    {base.name} / Variant {treatment.toUpperCase()}
                  </span>
                  <span>{fmt.label}</span>
                </div>
                <div className='mojo-canvas-stage'>
                  {/* Artwork: these four custom properties carry the mock ad's
                      own palette and are identical in both themes. */}
                  <article
                    className='mojo-creative'
                    style={
                      {
                        '--creative-bg': active.bg,
                        '--creative-accent': active.accent,
                        '--creative-secondary': active.secondary,
                        '--creative-ink': active.ink,
                        aspectRatio: fmt.ratio,
                      } as CSSProperties
                    }
                  >
                    <div className='mojo-creative-art'>
                      <i className='mojo-creative-orb' />
                      <i className='mojo-creative-shape' />
                      <i className='mojo-creative-star' />
                    </div>
                    <div className='mojo-creative-copy'>
                      <small>MOJO AI · FOR {audience.toUpperCase()}</small>
                      <h3>{copy.headline}</h3>
                      <p>{copy.body}</p>
                    </div>
                    <span className='mojo-creative-cta'>{copy.cta}</span>
                  </article>
                </div>
                <div className='mojo-canvas-caption'>
                  <span>
                    {base.name} · {audience} · {messageLabel}
                  </span>
                  <span>Preview only · synthetic content</span>
                </div>
              </section>

              <aside className='mojo-studio-panel'>
                <div className='mojo-panel-head'>02 · Brand analysis</div>
                <div className='mojo-audit-list'>
                  {audit.map(([label, score, status]) => (
                    <div className='mojo-audit-row' key={label}>
                      <span>
                        <span>{label}</span>
                        <strong>{score}/100</strong>
                      </span>
                      <div className='mojo-audit-track'>
                        <i
                          style={
                            {
                              '--score': `${score}%`,
                              '--status': status,
                            } as CSSProperties
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className='mojo-panel-head'>03 · Visual treatments</div>
                <div className='mojo-variant-grid'>
                  {variants.map(v => (
                    <button
                      key={v.id}
                      className='mojo-variant-button'
                      type='button'
                      aria-pressed={v.id === treatment}
                      onClick={() => setTreatment(v.id)}
                    >
                      <i
                        className='mojo-variant-preview'
                        style={
                          {
                            '--vbg': v.bg,
                            '--va': v.accent,
                            '--vi': v.ink,
                          } as CSSProperties
                        }
                      />
                      <span>{v.name}</span>
                    </button>
                  ))}
                </div>
              </aside>
            </div>

            <div className='mojo-studio-foot'>
              <span>
                Selected · {base.name} / Variant {treatment.toUpperCase()}
              </span>
              <span>Synthetic concept · not a shipping feature list</span>
            </div>
            <p className='mojo-sr-status' aria-live='polite'>
              {`Preview updated. ${base.name}, variant ${treatment.toUpperCase()}, ${fmt.label}, audience ${audience}, message ${messageLabel}. Synthetic concept only.`}
            </p>
          </div>
        </div>
      </section>

      <section className='mojo-section' id='workflow'>
        <div className='mojo-wrap'>
          <div className='mojo-section-head'>
            <div>
              <p className='mojo-label'>Conceptual workflow</p>
              <h2 className='mojo-display'>
                Template in. Controlled possibilities out.
              </h2>
            </div>
            <p className='mojo-section-copy'>
              A product interpretation grounded in the published positioning and
              technology list. These stages demonstrate how the idea could work;
              they are not audited implementation details.
            </p>
          </div>
          <div className='mojo-flow'>
            <article className='mojo-flow-card'>
              <span>MASTER</span>
              <h3>Read the template</h3>
              <p>
                Identify editable regions, hierarchy, components, and
                constraints.
              </p>
              <code>FIGMA PLUGIN API</code>
            </article>
            <article className='mojo-flow-card'>
              <span>INTENT</span>
              <h3>Route the message</h3>
              <p>
                Adapt the headline and support copy for audience and objective.
              </p>
              <code>OPENAI GPT-5</code>
            </article>
            <article className='mojo-flow-card'>
              <span>RULES</span>
              <h3>Analyze the design</h3>
              <p>
                Check contrast, spacing, hierarchy, overflow, and brand tokens.
              </p>
              <code>TYPESCRIPT</code>
            </article>
            <article className='mojo-flow-card'>
              <span>CHOICE</span>
              <h3>Review the range</h3>
              <p>
                Compare directions, refine the selected output, and keep human
                approval.
              </p>
              <code>SHADCN/UI</code>
            </article>
          </div>
        </div>
      </section>

      <section className='mojo-section'>
        <div className='mojo-wrap'>
          <div className='mojo-section-head'>
            <div>
              <p className='mojo-label'>Published technology stack</p>
              <h2 className='mojo-display'>
                Built inside the creative workflow.
              </h2>
            </div>
            <p className='mojo-section-copy'>
              Technology names are retained exactly from the live page. The
              descriptions below explain the redesign concept, not verified
              production architecture.
            </p>
          </div>
          <div className='mojo-tech-board'>
            <article className='mojo-tech-cell'>
              <span>01</span>
              <h3>TypeScript</h3>
              <p>Typed creative rules, state, and editor interactions.</p>
            </article>
            <article className='mojo-tech-cell'>
              <span>02</span>
              <h3>Figma Plugin API</h3>
              <p>Template context and design-tool integration.</p>
            </article>
            <article className='mojo-tech-cell'>
              <span>03</span>
              <h3>OpenAI GPT-5</h3>
              <p>Published model reference for content generation.</p>
            </article>
            <article className='mojo-tech-cell'>
              <span>04</span>
              <h3>shadcn/ui</h3>
              <p>Interface foundations for the product controls.</p>
            </article>
          </div>
        </div>
      </section>
    </div>
  )
}
